// Ruta: packages/shared/src/utils/index.ts

import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

// ─── Tailwind utility ─────────────────────────────────────────────────────────

/**
 * Merges Tailwind CSS classes using clsx + tailwind-merge.
 * Resolves conflicts (e.g. "p-2 p-4" → "p-4").
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

// ─── Currency ─────────────────────────────────────────────────────────────────

/**
 * Formats a number as currency.
 * Default: COP (Colombian Peso) with no decimal places in Colombian locale.
 */
export function formatCurrency(value: number, currency = 'COP', locale = 'es-CO'): string {
  if (currency === 'COP') {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

// ─── Date formatting ─────────────────────────────────────────────────────────

/**
 * Formats an ISO 8601 date string to a human-readable Spanish date.
 */
export function formatDate(isoString: string, locale = 'es-CO'): string {
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return 'Fecha inválida'
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone: 'America/Bogota',
  })
}

/**
 * Formats an ISO 8601 date string to a human-readable Spanish datetime.
 */
export function formatDateTime(isoString: string, locale = 'es-CO'): string {
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return 'Fecha inválida'
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Bogota',
  })
}

/**
 * Returns a relative time string ("hace 5 minutos", "hace 2 días", etc.)
 */
export function formatRelativeTime(isoString: string, locale = 'es-MX'): string {
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return 'Fecha inválida'

  const now = Date.now()
  const diffMs = now - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

  if (diffSeconds < 60) return rtf.format(-diffSeconds, 'second')
  const diffMinutes = Math.floor(diffSeconds / 60)
  if (diffMinutes < 60) return rtf.format(-diffMinutes, 'minute')
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return rtf.format(-diffHours, 'hour')
  const diffDays = Math.floor(diffHours / 24)
  return rtf.format(-diffDays, 'day')
}

// ─── Derived metrics ─────────────────────────────────────────────────────────

/**
 * Calculates profit margin as a percentage.
 * margin = (profit / amount) * 100
 * Returns 0 if amount is 0 to avoid division by zero.
 */
export function calcMargin(profit: number, amount: number): number {
  if (amount === 0) return 0
  return (profit / amount) * 100
}

/**
 * Formats a percentage with N decimal places.
 */
export function formatPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`
}

/**
 * Checks if a material/inventory item is below threshold (low stock).
 */
export function isLowStock(weight: number, threshold: number): boolean {
  return weight < threshold
}

/**
 * Returns the alert severity level based on weight vs threshold.
 * - critical: weight < threshold, or weight is 0 with threshold > 0
 * - warning: weight < threshold * 1.2 (within 20% of threshold)
 * - normal: above warning zone, or slot is inactive (both 0)
 */
export function getStockSeverity(
  weight: number,
  threshold: number
): 'normal' | 'warning' | 'critical' {
  // Empty slot with a threshold set — needs refill
  if (weight === 0 && threshold > 0) return 'critical'
  if (weight < threshold) return 'critical'
  if (weight < threshold * 1.2) return 'warning'
  return 'normal'
}

// ─── Weight formatting ────────────────────────────────────────────────────────

export function formatWeight(grams: number): string {
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(2)} kg`
  }
  return `${grams} g`
}

// ─── Null display ─────────────────────────────────────────────────────────────

export function orND(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return 'N/D'
  return String(value)
}

// ─── Truncate text ────────────────────────────────────────────────────────────

export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength - 3)}...`
}
